import type { CSSProperties } from 'react';
import { Clock, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ContextMenu, MenuItem, Tooltip } from '@/shared/ui';
import css from './task-more-menu.module.scss';

type TaskMoreMenuProps = {
  onEdit: () => void;
  onDelete: () => void;
  onSnooze: () => void;
};

/**
 * Three-dot row action menu. Mirrors legacy MoreHorizontal dropdown
 * with Update + Delete items. Menu is portal-rendered to escape the
 * table's `overflow: hidden`; trigger is anchored via getBoundingClientRect
 * with right-edge clamp (same pattern as TaskPriorityCell).
 */
export const TaskMoreMenu = ({
  onEdit,
  onDelete,
  onSnooze,
}: TaskMoreMenuProps) => {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const viewportPadding = 8;
    const menuWidth = 160;
    // Anchor menu to trigger's right edge (legacy behavior), clamped
    // to viewport so we never overflow.
    const left = Math.max(
      viewportPadding,
      Math.min(
        rect.right - menuWidth,
        window.innerWidth - menuWidth - viewportPadding
      )
    );
    setOffset({
      left,
      position: 'fixed',
      top: rect.bottom + 4,
      minWidth: menuWidth,
      zIndex: 60,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-task-more-menu]')) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className={css.root}>
      <Tooltip content="More actions">
        <button
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label="More actions"
          className={css.trigger}
          onClick={() => setOpen((p) => !p)}
          ref={triggerRef}
          type="button"
        >
          <MoreHorizontal size={16} />
        </button>
      </Tooltip>
      {open && offset
        ? createPortal(
            <ContextMenu
              className={css.menu}
              data-task-more-menu
              style={offset}
            >
              <MenuItem
                icon={<Clock size={14} />}
                onClick={() => {
                  setOpen(false);
                  onSnooze();
                }}
              >
                Snooze
              </MenuItem>
              <MenuItem
                icon={<Pencil size={14} />}
                onClick={() => {
                  setOpen(false);
                  onEdit();
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                destructive
                icon={<Trash2 size={14} />}
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
              >
                Delete
              </MenuItem>
            </ContextMenu>,
            document.body
          )
        : null}
    </div>
  );
};
